/** @jsx jsx */
import * as React from 'react';
import { useSelect, UseSelectProps } from 'downshift';

import { jsx, css } from '@emotion/core';
import cx from 'classnames';
import { Clickable } from 'reakit/Clickable';
import Caret from '@clearkit/icons/glyphs/Caret';
import Checkmark from '@clearkit/icons/glyphs/Checkmark';

import { CKSelectListItem } from '../CKSelectListItem';
import { usePopover } from '../../hooks';

import {
  ItemBase,
  CommonSelectProps,
  CKPickerVariant,
} from '../../common/types';
import { defaultItemRenderer } from '../../common/utils';

interface CKSelectProps<T extends ItemBase>
  extends UseSelectProps<T>,
    CommonSelectProps<T> {
  isDisabled?: boolean;
  isSmall?: boolean;
  variant?: CKPickerVariant;
  disclosure?: JSX.Element;
  itemRenderer?: (item: T) => React.ReactNode;
  selectedItemRenderer?: (selected: T) => React.ReactNode;
}

function defaultSelectedItemRender<T extends ItemBase>(selected: T) {
  return selected.label;
}

export const CKSelect = <T extends ItemBase>(
  props: CKSelectProps<T>
): React.ReactElement<CKSelectProps<T>> => {
  const popperReference = React.useRef<HTMLButtonElement>(null);
  const popperPopover = React.useRef<HTMLDivElement>(null);
  const popper = usePopover({
    popperReference,
    popperPopover,
    popperOffset: props.popperOffset,
    popperPlacement: props.popperPlacement,
    popperModifiers: props.popperModifiers,
  });

  const {
    placeholder,
    leftIcon,
    disclosure,
    isDisabled = false,
    isSmall = false,
    variant = 'default',
    itemRenderer = defaultItemRenderer,
    selectedItemRenderer = defaultSelectedItemRender,
    itemMetaRenderer = null,
    isItemDisabled = () => false,
    popperWidth,
    popperZIndex = 10,
    ...downshiftProps
  } = props;

  const {
    isOpen,
    selectedItem,
    getToggleButtonProps,
    getLabelProps,
    getMenuProps,
    highlightedIndex,
    getItemProps,
  } = useSelect(downshiftProps);

  const outerPickerClass = cx({
    'ck-picker': !disclosure,
    'ck-picker--error': variant === 'error',
    'ck-picker--success': variant === 'success',
    'ck-picker--open': isOpen,
    'ck-picker--disabled': isDisabled,
    'ck-picker--small': isSmall,
  });

  const popperClass = cx('ck-popover', {
    'ck-popover--full-width': !popperWidth,
  });

  React.useEffect(() => {
    if (isOpen && popper.current) {
      popper.current?.forceUpdate();
    }
  }, [isOpen, popper]);

  return (
    <div
      className={outerPickerClass}
      css={
        disclosure &&
        css`
          display: inline-flex;
          position: relative;
        `
      }
    >
      {disclosure ? (
        <React.Fragment>
          <label className="ck-sr-only" {...getLabelProps()}>
            {placeholder}
          </label>
          {React.cloneElement(
            disclosure,
            getToggleButtonProps({ ref: popperReference })
          )}
        </React.Fragment>
      ) : (
        <Clickable
          disabled={isDisabled}
          className="ck-picker-toggle"
          css={css`
            width: 100%;
          `}
          {...getToggleButtonProps({
            ref: popperReference,
            /*
             * Downshift does not call preventDefault on the button onClick handler. When
             * CKSelect is used inside a <form> tag, the form will be submitted without this.
             */
            onClick(event) {
              event.preventDefault();
            },
          })}
        >
          <label className="ck-sr-only" {...getLabelProps()}>
            {placeholder}
          </label>
          {leftIcon && (
            <span className="ck-picker-icon ck-picker-icon--left">
              {leftIcon}
            </span>
          )}
          {selectedItem ? (
            <span className="ck-picker-value">
              {selectedItemRenderer(selectedItem)}
            </span>
          ) : (
            <span className="ck-picker-value ck-picker-value--placeholder">
              {placeholder}
            </span>
          )}
          <span className="ck-picker-icon ck-picker-icon--right">
            <Caret />
          </span>
        </Clickable>
      )}
      <div
        className={popperClass}
        {...getMenuProps({ ref: popperPopover })}
        css={css`
          visibility: ${isOpen ? 'visible' : 'hidden'};
          pointer-events: ${isOpen ? 'auto' : 'none'};
          z-index: ${popperZIndex};
          width: ${popperWidth ? popperWidth : '100%'};
          &:focus {
            outline: 0;
          }
        `}
      >
        <ul
          className="ck-menu ck-menu--options"
          css={css`
            outline: 0;
          `}
        >
          {isOpen &&
            downshiftProps.items.map((item, index) => (
              <CKSelectListItem
                key={`${item}${index}`}
                isHighlighted={highlightedIndex === index}
                isSelected={selectedItem?.value === item.value}
                isDisabled={isItemDisabled(item)}
                {...getItemProps({
                  item,
                  index,
                  disabled: isItemDisabled(item),
                })}
                itemMetaRenderer={itemMetaRenderer && itemMetaRenderer(item)}
                accessoryRenderer={
                  selectedItem?.value === item.value && <Checkmark />
                }
              >
                {itemRenderer(item)}
              </CKSelectListItem>
            ))}
        </ul>
      </div>
    </div>
  );
};
